/**
 * Add machine, Cloud VM, "Create with AWS" (ADR 0106 decisions 3, 3a, 5): with an AWS account
 * connected, a name, a size with its hourly estimate, a region, Ephemeral or Permanent and an
 * optional public repository, then one button. Orgus creates the VM with the same cloud-init a
 * person can paste, and it joins Machines by itself. Without a connection an owner or admin
 * connects AWS here once: the CloudFormation link, then the role it made.
 */
import { CircleCheck, Cloud, ExternalLink, Plug } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { AWS_DEFAULT_SIZE, AWS_ROLE_ARN_RE } from '@orgus/protocol';
import type { CloudConnection, CloudVm, CloudVmLifetime } from '@orgus/protocol';
import { BUTTON_SIZE, BUTTON_VARIANT, Button, CopyButton, Disclosure, ErrorState, Input, RowStatus, Segmented, Select, cn, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { connectAws, createVm, setupAws, useCloud } from './data.ts';
import type { CloudView } from './data.ts';
import { REGION_OPTIONS, sizeOptions, stoppedText } from './model.ts';

export const LIFETIME_HINT: Record<CloudVmLifetime, string> = {
  ephemeral: 'Deleted when idle for 30 minutes, or after 8 hours at most.',
  permanent: 'Kept until someone deletes it.',
};

/** The Cloud VM tab's second section. `suggestName` gives the default name (a memorable one). */
export function CreateWithAws({ suggestName, onCreated }: { suggestName: () => string; onCreated?: (vm: CloudVm) => void }) {
  return <CreateWithAwsView cloud={useCloud()} suggestName={suggestName} onCreated={onCreated} />;
}

export function CreateWithAwsView({ cloud, suggestName, onCreated }: { cloud: CloudView; suggestName: () => string; onCreated?: (vm: CloudVm) => void }) {
  if (cloud.phase === 'missing') return null;
  return (
    <section aria-label="Create with AWS" className="flex flex-col gap-3" data-testid="create-with-aws">
      <div className="flex flex-col gap-1">
        <p className="text-ui font-medium text-fg">Create with AWS</p>
        <p className="text-caption text-fg-subtle">Orgus creates the VM in your AWS account; it joins by itself and AWS bills you directly.</p>
      </div>
      {cloud.phase === 'loading' ? (
        <RowStatus tone="working" icon="working" label="Loading the AWS connection" />
      ) : cloud.phase === 'error' ? (
        <ErrorState variant="inline" error={{ what: 'The AWS connection could not be read.', why: 'The console lost its connection to Orgus.', next: 'Reload the page.' }} />
      ) : !cloud.state?.provisioner.ready ? (
        <p className="text-caption text-fg-subtle" data-testid="aws-not-offered">
          {cloud.state?.provisioner.problem ?? 'This Orgus server cannot create VMs yet.'}
        </p>
      ) : cloud.aws?.state === 'connected' ? (
        cloud.canCreate ? (
          <CreateForm aws={cloud.aws} suggestName={suggestName} onCreated={onCreated} />
        ) : (
          <p className="text-caption text-fg-subtle">An operator, admin or owner creates VMs.</p>
        )
      ) : cloud.canConnect ? (
        <ConnectAws aws={cloud.aws} />
      ) : (
        <p className="text-caption text-fg-subtle">An owner or admin connects an AWS account once; then you can create VMs here.</p>
      )}
    </section>
  );
}

function CreateForm({ aws, suggestName, onCreated }: { aws: CloudConnection; suggestName: () => string; onCreated?: (vm: CloudVm) => void }) {
  const [name, setName] = useState(suggestName);
  const [size, setSize] = useState(AWS_DEFAULT_SIZE);
  const [region, setRegion] = useState(aws.region);
  const [lifetime, setLifetime] = useState<CloudVmLifetime>('ephemeral');
  const [repo, setRepo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [made, setMade] = useState<CloudVm | null>(null);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const vm = await createVm({ name: name.trim() || suggestName(), size, region, lifetime, ...(repo.trim() ? { repoUrl: repo.trim() } : {}) });
      setMade(vm);
      setName(suggestName());
      onCreated?.(vm);
    } catch (err) {
      setError(toUiError(err, { what: 'The VM was not created.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)} data-testid="aws-create-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="aws-vm-name" />
        <Select label="Size" value={size} onValueChange={setSize} options={sizeOptions(region)} />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-ui font-medium text-fg-muted">Lifetime</p>
        <Segmented
          label="Lifetime"
          value={lifetime}
          onChange={setLifetime}
          options={[
            { value: 'ephemeral', label: 'Ephemeral' },
            { value: 'permanent', label: 'Permanent' },
          ]}
          className="self-start"
        />
        <p className="text-caption text-fg-subtle">{LIFETIME_HINT[lifetime]}</p>
      </div>
      <Disclosure title="More options" summary={[region, repo.trim() ? 'clones a repository' : 'no repository'].join(', ')}>
        <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
          <Select label="Region" value={region} onValueChange={setRegion} options={REGION_OPTIONS} />
          <Input label="Repository" placeholder="Optional, https://github.com/org/repo" value={repo} onChange={(e) => setRepo(e.target.value)} />
        </div>
      </Disclosure>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" icon={<Cloud />} loading={busy} data-testid="aws-create">
          Create VM
        </Button>
        <p className="text-caption text-fg-subtle">
          AWS account {aws.accountId}
          {stoppedText(region) ? `. Stopped, it still pays ${stoppedText(region)}` : ''}
        </p>
      </div>
      {error && <ErrorState variant="inline" error={error} onRetry={() => void submit()} />}
      {made && !error && (
        <div role="status" aria-live="polite" data-testid="aws-created">
          <RowStatus tone="working" icon="working" label={`${made.name} is starting: it joins Machines in about two minutes`} />
          {made.sizeChange && <p className="text-caption text-fg-subtle" data-testid="aws-size-change">{made.sizeChange.message}</p>}
        </div>
      )}
    </form>
  );
}

export function ConnectAws({ aws, startOpen = false }: { aws: CloudConnection | null; startOpen?: boolean }) {
  const [open, setOpen] = useState(!!aws || startOpen);
  const [conn, setConn] = useState<CloudConnection | null>(aws);
  const [roleArn, setRoleArn] = useState('');
  const [busy, setBusy] = useState<'setup' | 'connect' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => setConn(aws), [aws]);

  const start = async () => {
    setOpen(true);
    if (conn) return;
    setBusy('setup');
    setError(null);
    try {
      setConn(await setupAws());
    } catch (err) {
      setError(toUiError(err, { what: 'AWS was not set up.' }));
    } finally {
      setBusy(null);
    }
  };

  // Opened from Machines: make the external id at once, so step 1 is ready.
  useEffect(() => {
    if (startOpen && !aws) void start();
    // Once, when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!conn) return;
    setBusy('connect');
    setError(null);
    try {
      await connectAws(roleArn.trim(), conn.region);
    } catch (err) {
      setError(toUiError(err, { what: 'AWS was not connected.' }));
    } finally {
      setBusy(null);
    }
  };

  if (!open) {
    return (
      <div className="flex flex-col gap-2">
        <Button variant="primary" icon={<Plug />} loading={busy === 'setup'} onClick={() => void start()} className="self-start" data-testid="aws-connect-start">
          Connect AWS
        </Button>
        {error && <ErrorState variant="inline" error={error} onRetry={() => void start()} />}
      </div>
    );
  }
  const arnOk = AWS_ROLE_ARN_RE.test(roleArn.trim());
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void connect(e)} data-testid="aws-connect">
      <ol className="flex flex-col gap-4">
        <li className="flex flex-col gap-2">
          <p className="text-ui text-fg">1. Create the Orgus role in your AWS account</p>
          <p className="text-caption text-fg-subtle">
            It lets Orgus create VMs and start, stop, restart or delete only the ones it tagged for this workspace. Orgus never holds a key of yours.
          </p>
          {conn?.quickCreateUrl ? (
            <a href={conn.quickCreateUrl} target="_blank" rel="noreferrer" className={cn(BUTTON_VARIANT.secondary, BUTTON_SIZE.md, 'self-start')} data-testid="aws-quick-create">
              <ExternalLink aria-hidden className="size-4" />
              Open CloudFormation
            </a>
          ) : (
            <p className="text-caption text-fg-subtle">Make a stack from the Orgus template with these parameters.</p>
          )}
          {conn && (
            <dl className="flex flex-col gap-1 text-caption">
              <Param label="External id" value={conn.externalId} />
              {!conn.quickCreateUrl && <Param label="Workspace id" value={conn.workspaceId} />}
              {!conn.quickCreateUrl && conn.principalArn && <Param label="Orgus principal" value={conn.principalArn} />}
            </dl>
          )}
        </li>
        <li className="flex flex-col gap-2">
          <p className="text-ui text-fg">2. Paste the role from the stack&apos;s outputs</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
            <Input
              label="Role ARN"
              placeholder="arn:aws:iam::123456789012:role/orgus-..."
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              wrapperClassName="sm:flex-1"
              data-testid="aws-role-arn"
            />
            <Button type="submit" variant="primary" icon={<CircleCheck />} loading={busy === 'connect'} disabled={!arnOk || !conn} data-testid="aws-connect-submit">
              Connect AWS
            </Button>
          </div>
        </li>
      </ol>
      {error && <ErrorState variant="inline" error={error} />}
    </form>
  );
}

function Param({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <dt className="shrink-0 text-fg-subtle">{label}</dt>
      <dd className="flex min-w-0 items-center gap-1">
        <code className="min-w-0 truncate font-mono text-fg">{value}</code>
        <CopyButton text={value} label={`Copy ${label.toLowerCase()}`} />
      </dd>
    </div>
  );
}
